import 'bugsnag-js';
import * as ng from 'angular';
import * as _ from 'underscore';
import * as $ from 'jquery';
import { StateService } from '@uirouter/angularjs';

import { ApiHelperService } from './api-helper.service';
import { naturalListOrder } from './natural-list-order';

export class SessionService {
   static instance:SessionService;

   static $inject = [
      '$state', 'apiHelper', 'localStorageService', 'toast', 'filterReset'
   ];

   constructor(
      private $state:StateService,
      private apiHelper:ApiHelperService,
      private localStorageService,
      private toast,
      private filterReset)
   {
      SessionService.instance = this;
   }

   user;

   orgMap:{};
   brandMap:{};
   campaignMap:{};

   // Listeners triggered when the Campaign is updated in the Menu
   private campaignUpdateListeners: ((campaign: any) => void)[] = [];

   loginRunning = false;

   /**
    * Returns the currently logged in user
    * @returns {object}
    */
   getUser() {
      return this.user;
   }

   isLoggedIn():boolean {
      return !!this.user && !!this.getToken();
   }

   /**
    * Replaces all session variables with a new user object
    * @param {object} user
    * @returns {session}
    */
   setUser(user) {
      this.user = user;

      this.orgMap = {};
      this.brandMap = {};
      this.campaignMap = {};

      let campaignId = this.localStorageService.get('campaignId');

      // pre-sorting objects for nice display in header dropdowns
      // keeping references to objects mapped by ID
      this.user.orgs.sort((a, b) => naturalListOrder(a.name, b.name));
      for (let org of this.user.orgs) {
         this.orgMap[org.id] = org;

         org.brands.sort((a, b) => naturalListOrder(a.name, b.name));
         for (let brand of org.brands) {
            this.brandMap[brand.id] = brand;

            brand.orgName = org.name;
            brand.campaigns.sort((a, b) => naturalListOrder(a.name, b.name));
            for (let campaign of brand.campaigns) {
               if (campaign.id != campaignId) {
                  delete campaign.settings;
                  delete campaign.hasTimeSlots;
                  delete campaign.ageRequirementType;
               }

               campaign.brandName = brand.name;
               this.campaignMap[campaign.id] = campaign;
            }
         }
      }

      if (!this.getOrg()) {
         this.setOrg(user.orgId);
      }

      if (!this.getBrand()) {
         if (this.getOrg().brands.length > 0) {
            this.setBrand(this.getOrg().brands[0].id);
         }
      }

      if (!this.getCampaign()) {
         if (this.getBrand() && this.getBrand().campaigns.length > 0) {
            this.setCampaign(this.getBrand().campaigns[0].id);
         }
      }

      Bugsnag.user = {
         id: this.user.id,
         name: this.user.handle,
         email: this.user.email
      };
   }

   /**
    * Returns true if the current users type matches one of the roles
    * specified in the comma delimited list. Passing '*' matches any
    * user type.
    * @param {string} roles comma delimited list of roles
    */
   isAuthorized(roles) {
      if (!this.isLoggedIn()) {
         return false;
      } else if (roles !== '*') {
         roles = roles.split(',');
         return _.contains(roles, this.user.role);
      }

      return true;
   }

   /**
    * Returns true if the current user is a super admin
    * @returns {boolean}
    */
   isSuperAdmin() {
      return this.user.role === 'SuperAdmin';
   }

   /**
    * Returns true if the current user is an organization admin
    * @returns {boolean}
    */
   isOrgAdmin() {
      return this.user.role === 'OrgAdmin';
   }

   /**
    * Returns true if the current user is a super or organization admin
    * @returns {boolean}
    */
   isAdmin() {
      return this.isSuperAdmin() || this.isOrgAdmin();
   }

   /**
    * Returns an organization object, parameter optional (will use local storage id if not present)
    * @param {int} id
    * @returns {object}
    */
   getOrg(id?:number) {
      let orgId = id ? id : this.localStorageService.get('orgId');
      return this.orgMap[orgId];
   }

   /**
    * Returns an array of all the organization objects this user has access to
    * @returns {array}
    */
   getOrgs() {
      return this.user.orgs;
   }

   /**
    * Replaces the user's current organization id in local storage
    * @param {int} id
    * @returns {session}
    */
   setOrg(id) {
      if (this.localStorageService.get('orgId') != id) {
         this.localStorageService.remove('brandId');
         this.localStorageService.remove('campaignId');
         this.localStorageService.set('orgId', id);
      }
   }

   /**
    * Returns a brand object, parameter optional (will use local storage id if not present)
    * @param {int} id
    * @returns {object}
    */
   getBrand(id?:number) {
      let brandId = id ? id : this.localStorageService.get('brandId');
      return this.brandMap[brandId];
   }

   /**
    * Returns an array of all the brand objects that match an organization id
    * @returns {array}
    */
   getBrandsByOrg(id) {
      if (!id) {
         return [];
      }

      let ids = id;
      if (!ng.isArray(ids)) {
         ids = [parseInt(id)];
      }

      let brands = [];
      for (let i of ids) {
         brands = brands.concat(this.orgMap[i].brands);
      }

      return brands;
   }

   /**
    * Replaces the user's current brand id in local storage
    * @param {int} id
    * @returns {session}
    */
   setBrand(id) {
      if (this.localStorageService.get('brandId') != id) {
         this.localStorageService.remove('campaignId');
         this.localStorageService.set('brandId', id);
      }
   }

   /**
    * Returns a campaign object, parameter optional (will use local storage id if not present)
    * @param {int} id
    * @returns {object}
    */
   getCampaign() {
      let campaignId = this.localStorageService.get('campaignId');
      return this.campaignMap[campaignId];
   }

   /**
    * Returns current campaignId
    */
   getCampaignId() {
      return this.localStorageService.get('campaignId');
   }

   /**
    * Returns an array of all the campaign objects that match a brand id
    * @returns {array}
    */
   getCampaignsByBrand(id) {
      if (!id) {
         return [];
      }

      let ids = id;
      if (!ng.isArray(ids)) {
         ids = [parseInt(id)];
      }

      let campaigns = [];
      for (let i of ids) {
         campaigns = campaigns.concat(this.brandMap[i].campaigns);
      }

      return campaigns;
   }

   /**
    * Replaces the user's current campaign id in local storage
    * @param {int} id
    * @returns {session}
    */
   setCampaign(id) {
      if (this.localStorageService.get('campaignId') != id) {
         let campaign = this.campaignMap[id];

         Bugsnag.metaData = {
            campaign: {
               id: campaign.id,
               name: campaign.name,
               slug: campaign.slug,
               brandName: this.getBrand().name,
               orgName: this.getOrg().name
            }
         };

         this.filterReset();
         this.localStorageService.set('campaignId', id);
         this.refreshCampaign();
      }
   }

   refreshCampaign() {
      let campaignId = this.getCampaignId();
      let campaign = this.campaignMap[campaignId];

      if (campaign && !campaign.settings) {
         this.apiHelper.get(`campaigns/${campaignId}`).then(campaign => {
            this.campaignMap[campaignId] = campaign;
            this.notifyCampaignListeners(campaign);
         });
      } else {
         this.notifyCampaignListeners(campaign);
      }
   }

   onCampaignUpdate(callback: (campaign: any) => void): () => void {
      this.campaignUpdateListeners.push(callback);

      // Notify the listener with the current campaign state if it exists
      if (this.getCampaign() && this.getCampaign().settings) {
         callback(this.getCampaign());
      }

      // Return an unsubscribe function
      return () => {
         this.campaignUpdateListeners = this.campaignUpdateListeners.filter(
            listener => listener !== callback
         );
      };
   }

   private notifyCampaignListeners(campaign: any): void {
      for (const listener of this.campaignUpdateListeners) {
         listener(campaign);
      }
   }

   /**
    * Returns the user's current OAuth token (if it exists)
    * @returns {string}
    */
   getToken() {
      return this.localStorageService.get('auth.token');
   }

   /**
    * Stores the user's current OAuth token
    * @param {string} token
    */
   setToken(token) {
      this.localStorageService.set('auth.token', token);
   }

   /**
    * Logs a user in via OAuth (grant type password) and creates their session
    * @param {string} username
    * @param {string} password
    * @returns {promise}
    */
   login(username, password) {
      var data = $.param({
         client_id: 'co.engage360', //TODO: setting
         grant_type: 'password', //TODO: setting
         username: username,
         password: password
      });

      this.loginRunning = true;
      return this.apiHelper.post('oauth/token', data).then(response => {
         this.setToken(response.access_token);

         //TODO: also save response.refresh_token, response.id_token and response.expires_in
         //TODO: create a user preference for this
         // clear all the saved filters on login to create a fresh "session"
         this.filterReset();

         return this.apiHelper.get('user').then(res => {
            this.setUser(res);
            this.refreshCampaign();
            return this.getUser();
         });
      }).finally(() => {
         this.loginRunning = false;
      })
   }

   refreshUser(defaults?) {
      return this.apiHelper.get('user').then(obj => {
         this.setUser(obj);
         if (typeof defaults === 'object') {
            if (defaults.orgId) {
               this.setOrg(defaults.orgId);
            }

            if (defaults.brandId) {
               this.setBrand(defaults.brandId);
            }

            if (defaults.campaignId) {
               this.setCampaign(defaults.campaignId);
            }
         } else {
            this.refreshCampaign();
         }
      });
   }

   /**
    * Generates a token and emails it to the user if they forgot their password
    * @param identity
    * @returns {*}
    */
   forgotPassword(identity) {
      return this.apiHelper.post('password/generate', { identity: identity });
   }

   /**
    * Sets a new password for the user if they have a valid token
    * @param token
    * @param password1
    * @param password2
    * @returns {*}
    */
   forgotVerify(token, password1, password2) {
      return this.apiHelper.post('password/verify', {
         token: token,
         password: password1,
         confirmPassword: password2
      });
   }

   /**
    * Destroys the session and redirects the user to the login page
    */
   logout(didExpire = false) {
      if (!this.localStorageService.get('auth.token')) {
         this.$state.go('login');
         return;
      }

      this.user = null;
      this.localStorageService.remove('auth.token');
      // NOTE: Anything else to remove?

      this.$state.go('login');

      if (didExpire) {
         this.toast.error()
            .title('Your session has ended')
            .message('You must sign in again to use E360')
            .send();
      }
   }

   getConfiguration(key):string {
      return this.localStorageService.get(key);
   }

   setConfiguration(key, value) {
      // NOTE: over doing it
      // if (this.localStorageService.get(key) != value) {
      //    this.localStorageService.remove(key);
      //    this.localStorageService.set(key, value);
      // }
      this.localStorageService.set(key, value);
   }

   // dynamic configuration retrieval functions
   angularVersion():string { return this.getConfiguration('angularVersion'); }
   apiDomain():string { return this.getConfiguration('apiDomain'); }
   appVersion():string { return this.getConfiguration('appVersion'); }
   bugSnagReleaseStage():string { return this.getConfiguration('bugSnagReleaseStage'); }
   defaultMediaDomain():string { return this.getConfiguration('defaultMediaDomain'); }
   deploymentName():string { return this.getConfiguration('deploymentName'); }
   enableBugSnag():string { return this.getConfiguration('enableBugSnag'); }
   googleApiKey():string { return this.getConfiguration('googleApiKey'); }
   joinVirtualDomain():string { return this.getConfiguration('joinVirtualDomain'); }
   qrDomain():string { return this.getConfiguration('qrDomain'); }
   rsvpRootDomain():string { return this.getConfiguration('rsvpRootDomain'); }
   selfRegRootDomain():string { return this.getConfiguration('selfRegRootDomain'); }
}

ng.module('e360')
   .service('session', SessionService);
